import React, { useCallback, useEffect, useState } from 'react';
import { v4 as uuidv4, validate as uuidValidate } from 'uuid';
import { produce } from 'immer';
import { useRoflStore } from 'store/roflStore';
import i18n from '@nextroll/ar-i18n';

import { ActionPropertyValuesInput, ComponentPropertyInputConfig, DefinitionInputPropertyFieldBase } from 'types/propertyGeneration';
import { MoreFieldsButton } from 'components/basic/MoreFieldsButton/MoreFieldsButton';
import { INodeBasePanelElementConfig } from 'components/basic/NodeBase/NodeBase';
import { defaultDropDownSearch } from 'components/basic/inputProperty/NodeDropDownSection/NodeDropDownSection';
import { InputProperty, RecordOperationType, WorkflowAction } from 'types/roflWorkflowActions';
import { ActionMetadataProperty, IPropertyError, NodeDropDownSectionProps, NodeDropDownSectionValue } from 'types/rofl';
import { RecordField, PropertyType, LookupType } from 'types/roflWorkflowActionProperty';
import { FieldSelector, FieldSelectorProps } from 'components/basic/inputProperty/definition/FieldSelector/FieldSelector';

import { NodeRecordFields, generateInitialMetadata } from 'store/nodeSlice';
import { TextInputDefinition, TextInputDefinitionUpdateWorkflowParams } from 'components/basic/inputProperty/definition/TextInputDefinition/TextInputDefinition';
import { RequestError, UseRequest } from 'hooks/useRequest';
import { DateInputDefinition, DateInputDefinitionUpdateWorkflowParams } from 'components/basic/inputProperty/definition/DateInputDefinition/DateInputDefinition';
import { ValidationError, generatePropertyErrorKey } from 'util/errors';
import { consoleWarn, getFieldMetadata, isFieldDefinitionRequired, isNullOrUndefined, getUpdatedMetadata, deleteFieldFromWorkflowConfig, deleteFieldFromMetadata } from 'util/util';

import useGenerateNodePanelDropDownConfig from './useGenerateNodePanelDropDownConfig';

/**
 * GenerateFieldSelectorConfig are the params for the useGenerateSingleLookupConfig hook
 */
export interface GenerateFieldSelectorConfig {
    serviceTitle: string | undefined;
    recordFields?: NodeRecordFields;
    propertyErrors: Record<string, IPropertyError> | undefined;
    workflowAction: WorkflowAction | undefined;
    call: UseRequest[0];
    authedWithoutMissingScopes: boolean; // is this node authed correctly without any missing scopes
    handleRequestError: (requestError: RequestError) => boolean;
    fieldInitialPropertyTextGenerated: Record<string, boolean>;
    setFieldInitialPropertyTextGenerated: React.Dispatch<React.SetStateAction<Record<string, boolean>>>;
}

interface FieldSelectorConfigParams {
    inputConfig: ComponentPropertyInputConfig;
    isUserDefinedField: boolean;
    fieldKey: string;
    availableFieldDropdownValues: NodeDropDownSectionValue[];
    field?: RecordField;
    definitionFieldConfig?: INodeBasePanelElementConfig;
    undeletable?: boolean;
}

type UUID = string;
type RecordFieldName = string;

const getFieldNameFromFieldKey = (fieldKey: string) => {
    return fieldKey.split('workload-service-node-panel-element-').pop();
};

/**
 * useGenerateFieldSelectorConfig returns functions related to generating the config for field selectors
 */
function useGenerateFieldSelectorConfig({
    serviceTitle,
    recordFields,
    propertyErrors,
    workflowAction,
    call,
    authedWithoutMissingScopes,
    handleRequestError,
    fieldInitialPropertyTextGenerated,
    setFieldInitialPropertyTextGenerated,
}: GenerateFieldSelectorConfig) {
    // Unselected FieldSelectors are initially defined in userDefinedFields as a UUID.
    // When a selection is made, the UUID in userDefinedFields is replaced with the field name (RecordFieldName).
    // If a field in userDefinedFields is a UUID, then we know it is an unselected FieldSelector.
    // If it is not, we know it has been replaced by a field name, and therefore has a selection.
    const [userDefinedFields, setUserDefinedFields] = useState<(UUID | RecordFieldName)[]>([]);
    // fieldIdentifierValid holds the association of whether or not a field (by identifier is valid or not)
    const [fieldIdentifierValid, setFieldIdentifierValid] = useState<Record<string, boolean>>({});
    const workflowActionEid = workflowAction?.eid || '';

    // fixedMissingFields keeps track of whether a missing field was fixed or not by a user so we know not to redisplay
    const [fixedMissingFields, setFixedMissingFields] = useState<Set<RecordFieldName>>(new Set());

    const [triggerHideRequiredError, setTriggerHideRequiredError] = useState(false);

    const hideRequiredError = useRoflStore(state => state.hideRequiredError);

    // cleanup
    useEffect(() => {
        setFieldIdentifierValid({});
        // Clear field states
        setUserDefinedFields([]);
        setFixedMissingFields(new Set());
        setTriggerHideRequiredError(false);
    }, [recordFields]);

    // tell react to call hideRequiredError after render, not during
    // this is a fix for the warning: Cannot update a component while rendering a different component.
    useEffect(() => {
        if (triggerHideRequiredError) {
            hideRequiredError();
        }
        setTriggerHideRequiredError(false);
    }, [hideRequiredError, triggerHideRequiredError]);

    /**
     *  generateNodePanelDropDownConfigFromRecordField dynamically generates
     *  node panel dropdowns from the passed in config
     */
    const {
        generateNodePanelDropDownConfigFromRecordField
    } = useGenerateNodePanelDropDownConfig({
        serviceTitle,
        workflowAction,
        call,
        propertyErrors,
        authedWithoutMissingScopes,
        handleRequestError,
        fieldInitialPropertyTextGenerated,
        setFieldInitialPropertyTextGenerated
    });

    /**
     *  updateActionConfigFieldValue saves field definition values into the workflow action config.
     *  If field has fields that are dependents, those dependents will be deleted from the config/metadata.
     */
    const updateActionConfigFieldValue = useCallback((
        inputConfig: ComponentPropertyInputConfig,
        field: RecordField,
        value: unknown,
        fieldDependents: Record<string, RecordField[]>,
        actionMetadataProperty?: ActionMetadataProperty
    ) => {
        let updatedMetadata = inputConfig.actionMetadata;
        const actionConfigValues = produce(inputConfig.workflowConfig ?? {}, draftWorkflowConfig => {
            draftWorkflowConfig[inputConfig.inputProperty.name] ??= {};
            draftWorkflowConfig[inputConfig.inputProperty.name][field.name] = value;
        });

        let updatedConfig = actionConfigValues;

        // Delete field's dependents
        if (fieldDependents[field.name]) {
            fieldDependents[field.name].forEach(dependent => {
                updatedConfig = deleteFieldFromWorkflowConfig(
                    updatedConfig,
                    inputConfig.inputProperty.name,
                    dependent
                );
                updatedMetadata = deleteFieldFromMetadata(
                    updatedMetadata || generateInitialMetadata(),
                    inputConfig.inputProperty.name,
                    dependent
                );
            });
        }

        const updatedActionMetadataValues = actionMetadataProperty?.fields?.[field.name]
            ? getUpdatedMetadata(inputConfig, field, actionMetadataProperty?.fields?.[field.name], updatedMetadata)
            : updatedMetadata;

        inputConfig.handleUpdateWorkflowNode(updatedConfig, updatedActionMetadataValues);
    }, []);

    /**
     *  deleteFieldAndUpdateWorkflowNode deletes a field from inputConfig's workflowConfig and the
     *  actionMetadata (if it is defined).
     *  It then updates the workflow node with the updated workflowConfig/actionMetadata.
     */
    const deleteFieldAndUpdateWorkflowNode = useCallback((
        inputConfig: ComponentPropertyInputConfig,
        field: RecordField,
    ) => {
        let newMetadata;
        const newConfig = deleteFieldFromWorkflowConfig(
            inputConfig.workflowConfig,
            inputConfig.inputProperty.name,
            field
        );
        if (inputConfig.actionMetadata) {
            newMetadata = deleteFieldFromMetadata(
                inputConfig.actionMetadata,
                inputConfig.inputProperty.name,
                field
            );
        }

        inputConfig.handleUpdateWorkflowNode(newConfig, newMetadata);
    }, []);

    const getDefinitionFieldConfig = useCallback((
        inputConfig: ComponentPropertyInputConfig,
        field: RecordField,
        fieldDependents: Record<string, RecordField[]>,
    ):
        INodeBasePanelElementConfig | undefined => {
        const fieldKey = `workload-service-node-panel-element-${field.name}`;
        const propertyError = propertyErrors?.[generatePropertyErrorKey(inputConfig.inputProperty.name, field.name)];
        const fieldRequired = isFieldDefinitionRequired(field, inputConfig.inputProperty.record_operation);
        const definitionComponentPropsPartial: Omit<DefinitionInputPropertyFieldBase<any>, 'updateWorkflow'> = {
            identifier: fieldKey,
            inputProperty: inputConfig.inputProperty,
            setIsValid: setFieldIdentifierValid,
            actionMetadataProperty: inputConfig.actionMetadataProperty,
            propertyError,
            field,
            workflowConfig: inputConfig.workflowConfig,
            workflowActionEid,
            serviceTitle,
            hideLabel: !fieldRequired
        };
        const getTextForNode = async () => {
            const { workflowConfig, actionMetadata, inputProperty: { name: propertyName = '' } } = inputConfig;
            const propertyConfig = workflowConfig?.[propertyName]?.[field.name];
            if (isNullOrUndefined(propertyConfig)) {
                return '';
            }
            // for Salesforce campaign fields, display field selections instead of display names
            // see ticket https://adroll.atlassian.net/browse/ORCH-805 for details
            if (propertyName === 'campaign_member_fields') {
                const propertyField = actionMetadata?.properties[propertyName]?.fields?.[field.name];
                return propertyField?.lookup?.options_lookup?.selected?.display_value || '';
            }
            return field.display_name;
        };
        const isNodeDropDownDefinition = field.enum || field.multivalued || field.lookup
                || field.type === PropertyType.BOOL;
        const isTextInputDefinition = (
            [PropertyType.STRING, PropertyType.INT, PropertyType.FLOAT] as (PropertyType | undefined)[]
        ).includes(field.type) && !isNodeDropDownDefinition;
        const isDateInputDefinition = (
            [PropertyType.DATE, PropertyType.DATETIME] as (PropertyType | undefined)[]
        ).includes(field.type) && !isNodeDropDownDefinition;

        // missing field doesn't render an inner component,
        // but instead has a required combobox to change to another field
        if (field.missing) {
            return {
                key: fieldKey,
                component: () => (
                    null
                ),
                isRequired: fieldRequired,
                validate: () => false,
                getTextForNode
            };
        } else if (isTextInputDefinition) {
            const definitionComponentProps: DefinitionInputPropertyFieldBase<
                TextInputDefinitionUpdateWorkflowParams
            >
                = {
                    ...definitionComponentPropsPartial,
                    // this function updates the node data for this field
                    updateWorkflow: (
                        { value }: TextInputDefinitionUpdateWorkflowParams
                    ) => {
                        if (value) {
                            updateActionConfigFieldValue(
                                inputConfig,
                                field,
                                field.type === PropertyType.INT || field.type === PropertyType.FLOAT
                                    ? Number(value) : value,
                                fieldDependents
                            );
                        } else {
                            deleteFieldAndUpdateWorkflowNode(inputConfig, field);
                        }
                    }
                };
            return {
                key: fieldKey,
                component: () => (
                    <TextInputDefinition
                        {...definitionComponentProps}
                    />
                ),
                isRequired: fieldRequired,
                validate: () => fieldIdentifierValid[fieldKey],
                getTextForNode
            };
        } else if (isDateInputDefinition) {
            const definitionComponentProps: DefinitionInputPropertyFieldBase<
                DateInputDefinitionUpdateWorkflowParams
            >
                = {
                    ...definitionComponentPropsPartial,
                    // this function updates the node data for the date field
                    updateWorkflow: (
                        { value }: DateInputDefinitionUpdateWorkflowParams
                    ) => {
                        updateActionConfigFieldValue(
                            inputConfig,
                            field,
                            value,
                            fieldDependents
                        );
                    }
                };
            return {
                key: fieldKey,
                component: () => (
                    <DateInputDefinition
                        {...definitionComponentProps}
                    />
                ),
                isRequired: fieldRequired,
                validate: () => fieldIdentifierValid[fieldKey],
                getTextForNode
            };
        } else if (isNodeDropDownDefinition) {
            // override onSelect for lookup since we aren't saving metadata for these fields
            const onSelect: NodeDropDownSectionProps['onSelect'] = (option: NodeDropDownSectionValue, _isOptionSelected, selectedOptions) => {
                if (selectedOptions) {
                    updateActionConfigFieldValue(
                        inputConfig,
                        field,
                        selectedOptions?.map(selectedOption => selectedOption.value),
                        fieldDependents
                    );
                } else {
                    const updatedMetadataPropertyLookup = getFieldMetadata(inputConfig, field, draftFields => {
                        const recordFieldMetadata = draftFields[field.name];
                        recordFieldMetadata.lookup ??= { type: LookupType.OPTIONS };
                        recordFieldMetadata.lookup.options_lookup = {
                            selected: { display_value: option.display_value } };
                    });
                    updateActionConfigFieldValue(
                        inputConfig,
                        field,
                        option.value,
                        fieldDependents,
                        updatedMetadataPropertyLookup
                    );
                }
            };
            let isDropdownDisabled = false;
            let actionPropertyValuesInput: ActionPropertyValuesInput = {
                record_field: field.name
            };

            if (field.depends_on && field.depends_on.length > 0) {
                isDropdownDisabled = true;
                if (inputConfig.workflowConfig) {
                    for (const dependency of field.depends_on) {
                        const fieldValue = inputConfig.workflowConfig[inputConfig.inputProperty.name]?.[dependency];
                        if (fieldValue) {
                            actionPropertyValuesInput = produce(
                                actionPropertyValuesInput,
                                draft => {
                                    draft.properties ??= {};
                                    draft.properties[inputConfig.inputProperty.name] ??= {};
                                    draft.properties[inputConfig.inputProperty.name][dependency] = fieldValue;
                                }
                            );
                        }
                    }
                    if (actionPropertyValuesInput.properties?.[inputConfig.inputProperty.name]
                        && Object.keys(actionPropertyValuesInput.properties?.[inputConfig.inputProperty.name]).length
                        === field.depends_on.length) {
                        isDropdownDisabled = false;
                    }
                }
            }

            // default onSearch function
            const onSearch = defaultDropDownSearch;

            const nodeBasePanelElementConfig = generateNodePanelDropDownConfigFromRecordField(
                inputConfig,
                field,
                isDropdownDisabled,
                actionPropertyValuesInput,
                {
                    getTextForNode,
                    nodeDropDownProps: {
                        hideLabel: !fieldRequired,
                        selected: inputConfig.workflowConfig?.[inputConfig.inputProperty.name]?.[field.name],
                        selectedDisplayValue: inputConfig.actionMetadataProperty?.fields?.[field.name]?.lookup
                            ?.options_lookup?.selected.display_value,

                        propertyError,
                        onSelect,
                        onSearch,
                    }
                }
            );
            return nodeBasePanelElementConfig;
        }
    }, [fieldIdentifierValid,
        deleteFieldAndUpdateWorkflowNode,
        generateNodePanelDropDownConfigFromRecordField,
        propertyErrors,
        updateActionConfigFieldValue,
        workflowActionEid,
        serviceTitle,
    ]);

    /**
     * syncActionConfig updates the config and metadata of the definitions shown on screen
     * to account for things like missing fields and out of sync metadata indexes
     */
    const syncActionConfig = useCallback(
        (
            fieldNamesWithValues: string[],
            inputConfig: ComponentPropertyInputConfig
        ) => {
            if (fieldNamesWithValues.length > 0 || fixedMissingFields.size > 0) {
                const metadata = inputConfig.actionMetadata || generateInitialMetadata();
                let updateWorkflowConfig = false;

                // delete any existing missing fields from the config
                const newWorkflowConfig = produce(inputConfig.workflowConfig, draftWorkflowConfig => {
                    for (const missingFieldName of fixedMissingFields) {
                        if (draftWorkflowConfig[inputConfig.inputProperty.name][missingFieldName] !== undefined) {
                            delete draftWorkflowConfig[inputConfig.inputProperty.name][missingFieldName];
                            updateWorkflowConfig = true;
                        }
                    }
                });

                const newMetadata = produce(metadata, draftMetadata => {
                    const draftMetadataProperties = draftMetadata.properties;

                    // delete any existing missing fields from the metadata
                    for (const missingFieldName of fixedMissingFields) {
                        if (draftMetadata.properties[inputConfig.inputProperty.name]?.fields?.[missingFieldName]) {
                            delete draftMetadata.properties[inputConfig.inputProperty.name]?.fields?.[missingFieldName];
                            updateWorkflowConfig = true;
                        }
                    }

                    // look for any out of sync or missing indexes in the metadata for the fields
                    // that are on the screen and update them
                    for (let fieldIndex = 0; fieldIndex < fieldNamesWithValues.length; fieldIndex++) {
                        const fieldNameWithValue = fieldNamesWithValues[fieldIndex];
                        const field = draftMetadata.properties[inputConfig.inputProperty.name]
                            ?.fields?.[fieldNameWithValue];
                        const indexMismatch = field?.index !== fieldIndex;
                        if (indexMismatch) {
                            draftMetadataProperties[inputConfig.inputProperty.name] ??= {};
                            const draftMetadataProperty = draftMetadataProperties[inputConfig.inputProperty.name];
                            draftMetadataProperty.fields ??= {};
                            draftMetadataProperty.fields[fieldNameWithValue]
                                = draftMetadataProperty.fields[fieldNameWithValue] ?? {};
                            draftMetadataProperty.fields[fieldNameWithValue].index = fieldIndex;
                            updateWorkflowConfig = true;
                        }
                    }
                });

                if (updateWorkflowConfig) {
                    inputConfig.handleUpdateWorkflowNode(newWorkflowConfig, newMetadata);
                }
            }
        }, [fixedMissingFields]);


    /**
     * createFieldSelectorConfig is responsible for:
     * 1. required, initial field selectors,
     * 2. optional, user defined field selectors,
     * 3. field selectors in which no selection has been made (unselected field selectors).
     */
    const createFieldSelectorConfig = useCallback(
        (fieldSelectorConfigParams: FieldSelectorConfigParams): INodeBasePanelElementConfig => {
            const {
                inputConfig,
                isUserDefinedField,
                fieldKey,
                availableFieldDropdownValues,
                field,
                definitionFieldConfig,
                undeletable,
            } = fieldSelectorConfigParams;

            const isUnselected = !field;
            const isInitial = !!field && !isUserDefinedField;
            const fieldRequired = !!(field
                && isFieldDefinitionRequired(field, inputConfig.inputProperty.record_operation));
            let data;
            if (isUnselected) {
                data = availableFieldDropdownValues;
            } else if (isUserDefinedField) {
                data = [{
                    display_value: field.display_name,
                    value: field.name,
                }].concat(availableFieldDropdownValues);
            } else {
                data = [{
                    display_value: field.display_name,
                    value: field.name,
                }];
            }

            // the only field selector error right now is FIELD_MISSING
            let fieldSelectorPropertyError: IPropertyError | undefined;
            if (field?.missing) {
                const propertyError
                        = propertyErrors?.[generatePropertyErrorKey(inputConfig.inputProperty.name, field.name)];
                if (propertyError?.error === ValidationError.FIELD_MISSING) {
                    fieldSelectorPropertyError = propertyError;
                }
            }

            // Field selectors that are not selected have no field (thus, no field.name) associated with them.
            // These unselected field selectors instead pass in their UUID as the fieldKey.
            // When a field is selected, their UUID in userDefinedFields is replaced by the field name.
            const fieldSelectorComponentProps: FieldSelectorProps = {
                // don't allow deleting of missing fields or the required, initial field selectors,
                deletable: !undeletable && (!isInitial && !field?.missing),
                entityTitle: field?.display_name,
                propertyError: fieldSelectorPropertyError,
                hideNodeDropDown: fieldRequired,
                onDelete: (isInitial || undeletable) ? () => { }
                    : () => {
                        setUserDefinedFields(
                            produce(draft => {
                                if (!isUnselected) {
                                    deleteFieldAndUpdateWorkflowNode(inputConfig, field);
                                    // Selected fields have their field names in userDefinedFields
                                    return draft.filter(item => item !== field.name);
                                } else {
                                    // Unselected fields have their UUID field key in userDefinedFields instead
                                    return draft.filter(item => item !== fieldKey);
                                }
                            })
                        );
                    },
                nodeDropDownProps: {
                    identifier: fieldKey,
                    setIsValid: () => true,
                    selected: !isUnselected && field.name,
                    onSelect: isInitial
                        ? (): void => {}
                        : (updateParams: NodeDropDownSectionValue) => {
                            setUserDefinedFields(
                                produce(draft => {
                                    if (!isUnselected) {
                                        deleteFieldAndUpdateWorkflowNode(inputConfig, field);

                                        // If the field was previously missing,
                                        // set it as fixed so we don't redisplay
                                        if (field.missing) {
                                            setFixedMissingFields(
                                                produce(draftFixedMissingFields => {
                                                    draftFixedMissingFields.add(field.name);
                                                })
                                            );
                                        }
                                        // Selected fields have their field names in userDefinedFields
                                        return draft.map(item => {
                                            return item === field.name ? updateParams.value : item;
                                        });
                                    } else {
                                        // Unselected fields have their UUID field key in userDefinedFields instead
                                        return draft.map(item => {
                                            return item === fieldKey ? updateParams.value : item;
                                        });
                                    }
                                })
                            );
                        },
                    serviceTitle: serviceTitle || '',
                    entityTitle: isUnselected ? i18n.gettext('Field Name') : i18n.gettext('Field'),
                    dropdownTitle: i18n.gettext('Field Name'),
                    placeholder: i18n.gettext('Select Field'),
                    allowRefresh: false,
                    hideLabel: fieldRequired,
                    data,
                }
            };
            return {
                key: fieldKey,
                component: () => (
                    <>
                        {!isInitial && <hr />}
                        <FieldSelector
                            {...fieldSelectorComponentProps}
                        >
                            {definitionFieldConfig && definitionFieldConfig.component()}
                        </FieldSelector>
                    </>
                ),
                isRequired: definitionFieldConfig?.isRequired,
                validate: definitionFieldConfig?.validate || (() => !isUnselected),
                getTextForNode: definitionFieldConfig?.getTextForNode
            };
        }, [
            deleteFieldAndUpdateWorkflowNode,
            propertyErrors,
            serviceTitle,
        ]);

    type UserDefinedFieldSelectorConfigMap
        = Record<string, {config: INodeBasePanelElementConfig; fieldMissing: boolean}>;
    /**
     * processUserDefinedFields pushes userDefinedFields configs to userDefinedFieldSelectorConfigs
     * and preserves order within userDefinedFields
     */
    const processUserDefinedFields = useCallback(
        (
            userDefinedConfigs: UserDefinedFieldSelectorConfigMap,
            inputConfig,
            orderedFieldNames,
            remainingFieldDropdownValues,
        ) => {
            const userDefinedFieldSelectorConfigs: INodeBasePanelElementConfig[] = [];
            // Push userDefinedFields configs to userDefinedFieldSelectorConfigs
            // separately to preserve order within userDefinedFields
            userDefinedFields.forEach(field => {
                if (userDefinedConfigs[field]) {
                    if (!isNullOrUndefined(inputConfig.workflowConfig?.[
                        inputConfig.inputProperty.name]?.[field])) {
                        // collect fields with values in order to update the action config later
                        if (!userDefinedConfigs[field].fieldMissing) {
                            orderedFieldNames.push(field);
                        }
                    }
                    userDefinedFieldSelectorConfigs.push(userDefinedConfigs[field].config);
                } else if (uuidValidate(field)) {
                    // Unselected fields get an empty FieldSelector component
                    const config = createFieldSelectorConfig(
                        {
                            inputConfig,
                            isUserDefinedField: false,
                            fieldKey: field,
                            availableFieldDropdownValues: remainingFieldDropdownValues,
                        }
                    );
                    userDefinedFieldSelectorConfigs.push(config);
                }
            });
            return userDefinedFieldSelectorConfigs;
        }, [userDefinedFields, createFieldSelectorConfig]);

    /**
     *  generateFieldSelectorConfig dynamically generates field selector configs
     */
    const generateFieldSelectorConfig = useCallback(
        (inputConfig: ComponentPropertyInputConfig): INodeBasePanelElementConfig[] | undefined => {
            if (!recordFields) {
                return;
            }

            const recordFieldsWithDependencies = recordFields[inputConfig.inputProperty.name];
            if (!recordFieldsWithDependencies?.fields) {
                return;
            }

            // Create map with the dependents of each field
            const fieldDependents: Record<string, RecordField[]> = {};
            for (const field of recordFieldsWithDependencies.fields) {
                if (field.depends_on) {
                    field.depends_on.forEach(dependency => {
                        if (!fieldDependents[dependency]) {
                            fieldDependents[dependency] = [field];
                        } else {
                            fieldDependents[dependency].push(field);
                        }
                    });
                }
            }

            const remainingFieldDropdownValues: NodeDropDownSectionValue[] = [];
            const initialFieldSelectorConfigs: INodeBasePanelElementConfig[] = [];

            const fieldNamesWithValues: string[] = [];
            const fieldToDefinitionFieldConfig: Record<string, INodeBasePanelElementConfig> = {};

            let possibleOptionalFieldsCount = 0;
            for (const field of recordFieldsWithDependencies.fields) {
                const definitionFieldConfig = getDefinitionFieldConfig(inputConfig, field, fieldDependents);
                if (definitionFieldConfig) {
                    // Assemble definitionFieldConfigs
                    fieldToDefinitionFieldConfig[field.name] = definitionFieldConfig;
                } else {
                    consoleWarn(`failed to get definitionFieldConfig for ${field.name}`);
                    continue;
                }

                const userFieldAlreadyDefined = userDefinedFields.includes(field.name);
                // Add to the fieldSelector's remaining fields if this field isn't required for the operation
                // and not missing
                if (!field.missing && !isFieldDefinitionRequired(field, inputConfig.inputProperty.record_operation)) {
                    possibleOptionalFieldsCount++;
                    if (!userFieldAlreadyDefined) {
                        remainingFieldDropdownValues.push({
                            value: field.name,
                            display_value: field.display_name,
                        });
                    }
                }
            }

            const userDefinedConfigs: UserDefinedFieldSelectorConfigMap = {};
            let fieldWasLoadedFromMetadata = false;
            // display the required, initial fields and the selected fields
            for (const field of recordFieldsWithDependencies.fields) {
                if (!fieldToDefinitionFieldConfig[field.name]) {
                    continue;
                }

                const fieldKey = `workload-service-node-panel-element-${field.name}`;
                const fieldRequired = isFieldDefinitionRequired(field, inputConfig.inputProperty.record_operation);
                const isUserDefinedField = userDefinedFields.includes(field.name);
                // render the missing field only if it hasn't been 'fixed'
                const fieldHasNotBeenFixed = field.missing && !fixedMissingFields.has(field.name);

                // If the field is in the action metadata, is not required, and has a value
                // add it to userDefinedFields if it not already in
                if (
                    !isUserDefinedField
                    && ((
                        inputConfig.actionMetadata?.properties[
                            inputConfig.inputProperty.name]?.fields?.[field.name] !== undefined
                        && !fieldRequired
                    ) || fieldHasNotBeenFixed)
                ) {
                    fieldWasLoadedFromMetadata = true;
                    setUserDefinedFields(
                        produce(draft => {
                            draft.push(field.name);
                        })
                    );
                }

                if (fieldRequired && !isUserDefinedField) {
                    // Initial, required fields
                    const config = createFieldSelectorConfig({
                        inputConfig,
                        isUserDefinedField,
                        fieldKey,
                        availableFieldDropdownValues: remainingFieldDropdownValues,
                        field,
                        definitionFieldConfig: fieldToDefinitionFieldConfig[field.name],
                    });
                    initialFieldSelectorConfigs.push(config);
                    // collect fields with values in order to update the action config later
                    if (!isNullOrUndefined(inputConfig.workflowConfig?.[
                        inputConfig.inputProperty.name]?.[field.name])) {
                        if (!field.missing) {
                            fieldNamesWithValues.push(field.name);
                        }
                    }
                } else if (isUserDefinedField) {
                    // User defined fields
                    const config = createFieldSelectorConfig({
                        inputConfig,
                        isUserDefinedField,
                        fieldKey,
                        availableFieldDropdownValues: remainingFieldDropdownValues,
                        field,
                        definitionFieldConfig: fieldToDefinitionFieldConfig[field.name],
                    });
                    userDefinedConfigs[field.name] = {
                        config,
                        fieldMissing: !!field.missing,
                    };
                }
            }

            const userCreatedFieldSelectorConfigs = processUserDefinedFields(
                userDefinedConfigs,
                inputConfig,
                fieldNamesWithValues,
                remainingFieldDropdownValues,
            );

            const nodeBasePanelElementConfigs: INodeBasePanelElementConfig[] = [
                ...initialFieldSelectorConfigs,
                ...userCreatedFieldSelectorConfigs,
            ];

            const isCreateOrUpsert = (
                [RecordOperationType.CREATE, RecordOperationType.UPSERT] as (RecordOperationType | undefined)[]
            ).includes(inputConfig.inputProperty.record_operation);

            // Make first field selector undeletable if there are field selectors
            // or add a new field selector, which will become undeletable as the first field selector
            if (!isCreateOrUpsert) {
                if (nodeBasePanelElementConfigs.length > 0) {
                    // Re-create the config
                    let newConfig;
                    const oldConfig = nodeBasePanelElementConfigs[0];
                    const fieldKey = oldConfig.key;
                    if (fieldKey) {
                        if (uuidValidate(fieldKey)) {
                            newConfig = createFieldSelectorConfig({
                                inputConfig,
                                isUserDefinedField: false,
                                fieldKey,
                                availableFieldDropdownValues: remainingFieldDropdownValues,
                                undeletable: true,
                            });
                        } else {
                            const fieldName = getFieldNameFromFieldKey(fieldKey);
                            if (fieldName) {
                                newConfig = createFieldSelectorConfig({
                                    inputConfig,
                                    isUserDefinedField: userDefinedFields.includes(fieldName),
                                    fieldKey,
                                    availableFieldDropdownValues: remainingFieldDropdownValues,
                                    field: recordFieldsWithDependencies.fields.find(field => field.name === fieldName),
                                    definitionFieldConfig: fieldToDefinitionFieldConfig[fieldName],
                                    undeletable: true,
                                });
                            }
                        }
                    }

                    if (newConfig) {
                        nodeBasePanelElementConfigs[0] = newConfig;
                    }
                } else if (!fieldWasLoadedFromMetadata) {
                    setTriggerHideRequiredError(true);
                    // Add a new field selector
                    setUserDefinedFields(
                        produce(draft => {
                            draft.push(uuidv4());
                        })
                    );
                }
            }

            const handleAddMoreFields = () => {
                const newFieldKey = uuidv4();
                setUserDefinedFields(
                    produce(draft => {
                        draft.push(newFieldKey);
                    })
                );
            };

            // Display More Fields Button logic
            if (userCreatedFieldSelectorConfigs.length < possibleOptionalFieldsCount
                && remainingFieldDropdownValues.length > 0
            ) {
                nodeBasePanelElementConfigs.push({
                    key: 'workload-service-node-panel-element-add-more-fields-btn',
                    component: () => (
                        <>
                            <hr />
                            <MoreFieldsButton
                                onClick={handleAddMoreFields}
                            />
                        </>
                    ),
                });
            }

            syncActionConfig(fieldNamesWithValues, inputConfig);
            return nodeBasePanelElementConfigs;
        }, [
            fixedMissingFields,
            getDefinitionFieldConfig,
            recordFields,
            syncActionConfig,
            userDefinedFields,
            processUserDefinedFields,
            createFieldSelectorConfig,
        ]);

    /**
     *  isFieldSelectorConfig returns whether or not the inputProperty will be handled by this generator
     */
    const isFieldSelectorConfig = useCallback((inputProperty: InputProperty): boolean => {
        return inputProperty.type === PropertyType.RECORD;
    }, []);

    return { generateFieldSelectorConfig, isFieldSelectorConfig };
}

export default useGenerateFieldSelectorConfig;
